import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './ResetPassword.css';
import maskGroupImage from '../../../assets/Mask group.png';
import ApiService from '../../services/ApiService';

const ResetPassword: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get code from URL parameters
  const code = searchParams.get('code');

  const getApiErrorMessage = (response: any, fallback: string) => {
    const messageFromData = response?.data?.message?.toString?.().trim();
    const directMessage = response?.message?.toString?.().trim();
    const errorField = response?.error?.toString?.().trim();
    return messageFromData || directMessage || errorField || fallback;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');

    // Validation
    if (!password.trim()) {
      setError('Password is required');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!code) {
      setError('Invalid or missing reset code');
      return;
    }

    setIsLoading(true);

    try {
      const apiService = ApiService.getInstance();
      const baseUrl = apiService.getAuthServiceUrl();

      const response = await apiService.post(
        'auth/reset-password',
        {
          token: code,
          new_password: password
        },
        baseUrl
      );

      if (response.success && response.data?.is_success) {
        setMessage(response.data.message || 'Password has been reset successfully. You can now log in with your new password.');

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(getApiErrorMessage(response, 'Failed to reset password. Please try again.'));
      }
    } catch (err) {
      console.error('Reset password error:', err);
      const errorMessage =
        (err as any)?.message?.toString?.().trim() ||
        'An unexpected error occurred. Please try again.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="reset-container">
      <div className="reset-main-content">
        {/* Left side - Image section */}
        <div className="reset-image-section">
          <div className="image-container">
            <img
              src={maskGroupImage}
              alt="Chaithanya Eye Hospital"
              className="reset-image"
            />
          </div>
        </div>

        {/* Right side - Form section */}
        <div className="reset-form-section">
          <div className="reset-card">
            <div className="reset-header">
              <h1>Reset Password</h1>
            </div>

            <form onSubmit={handleSubmit} className="reset-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {message && (
            <div className="success-message">
              {message}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="password">
              Password
              <span className="help-wrapper">
                <span className="help-icon" aria-label="Password policy" role="button" tabIndex={0}>?</span>
                <div className="password-policy-tooltip" role="dialog" aria-modal="false">
                  <div className="tooltip-title">Password Policy:</div>
                  <ul>
                    <li>Password must contain at least 8 characters.</li>
                    <li>Maximum password length is 30 characters.</li>
                    <li>Password must contain at least one number(0-9).</li>
                    <li>Password must contain at least one uppercase alphabet(A-Z).</li>
                    <li>Password must contain at least one lowercase alphabet(a-z).</li>
                    <li>Password must contain at least one special character(!@#$%^&*).</li>
                  </ul>
                </div>
              </span>
            </label>
            <div className="password-input-container">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                required
                disabled={isLoading}
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
              >
                {showPassword ? (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <div className="password-input-container">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm Password"
                required
                disabled={isLoading}
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={isLoading}
              >
                {showConfirmPassword ? (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="back-to-login-link"
              onClick={handleBackToLogin}
              disabled={isLoading}
            >
              Go back to Login
            </button>
          </div>

          <button
            type="submit"
            className="submit-button"
            disabled={isLoading || !password.trim() || !confirmPassword.trim()}
          >
            {isLoading ? 'Resetting...' : 'Submit'}
          </button>
        </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
